// pages/stations/stationShared.jsx — extracted from App.js (behaviour-preserving split).
import { useCallback, useEffect, useRef, useState } from "react";
import { AlertTriangle, ChefHat, LogOut, Wine } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { http } from "@/app/core";


// =============================================================================
// Kitchen Dashboard — bold, fast, optimized for service rush
// =============================================================================

// Session 30.21 — Station long-wait flag. One 15 s clock per board;
// tickets older than the owner-configured threshold get a pulsing red
// frame + "WAITING n MIN" badge so kitchen/bar share the floor's urgency.
export function useStationClock() {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 15000);
    return () => clearInterval(id);
  }, []);
  return now;
}
export function ticketWaitMinutes(order, now) {
  const from = order?.created_at || order?.placed_at;
  if (!from) return 0;
  const t = new Date(from).getTime();
  if (Number.isNaN(t)) return 0;
  return Math.max(0, Math.floor((now - t) / 60000));
}
export function LongWaitTicketBadge({ minutes, testId }) {
  const label = minutes < 60 ? `${minutes} min` : minutes < 1440 ? `${Math.floor(minutes / 60)}h ${String(minutes % 60).padStart(2, "0")}m` : `${Math.floor(minutes / 1440)}d+`;
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-rose-600 px-2.5 py-1 text-xs font-bold uppercase tracking-wide text-white shadow animate-pulse" data-testid={testId}>
      <AlertTriangle className="h-3.5 w-3.5" /> Waiting {label}
    </span>
  );
}

export function KitchenTimeAgo({ from }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 15000);
    return () => clearInterval(id);
  }, []);
  if (!from) return null;
  const d = new Date(from);
  if (Number.isNaN(d.getTime())) return null;
  const minutes = Math.max(0, Math.floor((now - d.getTime()) / 60000));
  if (minutes < 1) return <span>just now</span>;
  if (minutes < 60) return <span>{minutes} min ago</span>;
  const hours = Math.floor(minutes / 60);
  return <span>{hours}h {minutes % 60}m ago</span>;
}

// ---- Session 63 — Station department picker ------------------------------
// A venue may run Kitchen 1 + Kitchen 2 (or Bar 1 + Bar 2). Every time a
// kitchen/bar dashboard loads it asks which department this screen is
// operating. Deliberately NOT persisted (owner's choice): a refresh or
// reopen asks again, so a tablet moved to another pass never shows the
// wrong tickets.
export function StationDepartmentPicker({ station, departments, businessName, onSelect, onLogout }) {
  const isKitchen = station === "kitchen";
  const Icon = isKitchen ? ChefHat : Wine;
  return (
    <main className="min-h-screen bg-background px-4 py-5 sm:px-6 sm:py-7" data-testid={`${station}-department-picker`}>
      <div className="mx-auto flex w-full max-w-3xl flex-col gap-6">
        <header className="flex items-center gap-4 rounded-[28px] border border-border bg-card p-5 sm:p-6">
          <div className={`flex h-14 w-14 items-center justify-center rounded-2xl ${isKitchen ? "bg-[hsl(var(--primary))] text-primary-foreground" : "bg-indigo-600 text-white"}`}>
            <Icon className="h-7 w-7" />
          </div>
          <div className="min-w-0 flex-1">
            <Badge className={isKitchen ? "bg-[hsl(var(--primary))] text-primary-foreground" : "bg-indigo-600 text-white"}>{isKitchen ? "Kitchen station" : "Bar station"}</Badge>
            <h1 className="mt-2 font-['Space_Grotesk'] text-2xl font-semibold tracking-tight sm:text-3xl">{businessName || (isKitchen ? "Kitchen" : "Bar")}</h1>
            <p className="text-sm text-muted-foreground">Which {isKitchen ? "kitchen" : "bar"} is this screen running? Only that department's lines will show here.</p>
          </div>
          {onLogout ? (
            <Button variant="outline" className="rounded-xl border-destructive/40 text-destructive hover:bg-destructive/10 hover:text-destructive" onClick={onLogout} data-testid={`${station}-department-picker-logout`}>
              <LogOut className="h-4 w-4" />
              <span className="hidden sm:inline">Logout</span>
            </Button>
          ) : null}
        </header>
        <div className="grid gap-4 sm:grid-cols-2">
          {departments.map((d) => (
            <button
              key={d.id || d.name}
              type="button"
              onClick={() => onSelect(d)}
              className={`group flex min-h-[112px] flex-col items-start justify-between rounded-[24px] border-2 border-border bg-card p-5 text-left shadow-sm transition-[transform,box-shadow,border-color] duration-150 hover:-translate-y-0.5 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring active:translate-y-0 ${isKitchen ? "hover:border-[hsl(var(--primary))]" : "hover:border-indigo-500"}`}
              data-testid={`${station}-department-option-${String(d.name || "").toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            >
              <span className={`flex h-10 w-10 items-center justify-center rounded-xl ${isKitchen ? "bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))]" : "bg-indigo-500/10 text-indigo-600 dark:text-indigo-300"}`}>
                <Icon className="h-5 w-5" />
              </span>
              <span className="mt-3 font-['Space_Grotesk'] text-xl font-semibold leading-tight">{d.name}</span>
              <span className="mt-1 text-xs text-muted-foreground">Tap to open this department's tickets</span>
            </button>
          ))}
        </div>
      </div>
    </main>
  );
}

// Session 63 — one hook shared by the Kitchen and Bar dashboards: loads the
// station's departments, decides whether a picker is needed, and exposes a
// ref-backed `selected` so memoised fetchers / WS handlers never read a
// stale value. Selection lives in React state only (never persisted).
export function useStationDepartmentScope(station, headers) {
  const [departments, setDepartments] = useState(null); // null = loading
  const [selected, setSelected] = useState(null);       // department name or null (= whole station)
  const selectedRef = useRef(null);
  const namesRef = useRef([]);                           // lower-cased names of every station department
  const readyRef = useRef(false);                        // true once the scope is settled (fetching may begin)
  useEffect(() => {
    if (!headers) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const r = await http.get(`/${station}/departments`, { headers });
        if (cancelled) return;
        const list = Array.isArray(r.data?.departments) ? r.data.departments.filter((d) => d && d.name) : [];
        namesRef.current = list.map((d) => String(d.name).trim().toLowerCase());
        setDepartments(list);
        if (list.length <= 1) {
          // Nothing to choose — operate the whole station (legacy behaviour).
          selectedRef.current = null;
          setSelected(null);
          readyRef.current = true;
        }
      } catch (_) {
        // Offline / LAN-only: fall back to the whole station rather than
        // blocking the screen behind a picker it can't populate.
        if (cancelled) return;
        setDepartments([]);
        readyRef.current = true;
      }
    })();
    return () => { cancelled = true; };
  }, [station, headers]);
  const choose = useCallback((dept) => {
    const name = dept && dept.name ? String(dept.name).trim() : null;
    selectedRef.current = name;
    readyRef.current = true;
    setSelected(name);
  }, []);
  const reset = useCallback(() => {
    selectedRef.current = null;
    readyRef.current = false;
    setSelected(null);
  }, []);
  const needsPick = Array.isArray(departments) && departments.length > 1 && !selected;
  const loadingDepts = departments === null;
  // Narrow a ticket list to the chosen department (identity when unscoped).
  const scopeOrders = useCallback((list) => {
    const name = selectedRef.current;
    if (!name) return list;
    const lc = name.toLowerCase();
    const out = [];
    for (const o of list || []) {
      if (!o) continue;
      const all = o.items || [];
      const items = all.filter((it) => it && String(it.department || "").trim().toLowerCase() === lc);
      if (!items.length) continue;
      out.push(items.length === all.length ? o : { ...o, items });
    }
    return out;
  }, []);
  // Does this line belong to this screen? (WS order_created filter)
  const lineMatches = useCallback((it, fallbackName) => {
    if (!it || it.voided) return false;
    const dept = String(it.department || "").trim().toLowerCase();
    if (selectedRef.current) return dept === selectedRef.current.toLowerCase();
    if (namesRef.current.length) return namesRef.current.includes(dept);
    return dept === fallbackName;
  }, []);
  const scopeBody = useCallback(() => (selectedRef.current ? { scope_department: selectedRef.current } : {}), []);
  const scopeParams = useCallback(() => (selectedRef.current ? { department: selectedRef.current } : undefined), []);
  return { departments, selected, selectedRef, readyRef, needsPick, loadingDepts, choose, reset, scopeOrders, lineMatches, scopeBody, scopeParams };
}
